// src/components/ParameterGraph.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { KernelConfig, ParamSelection, HoveredPoint, PhaseData } from '../types/lenia';
import { setupCanvas, clearCanvas, drawBackground } from '../utils/canvasUtils';
import { CANVAS_CONFIG } from '../constants/canvas';
import { Legend } from './Legend';
import { PhaseKey } from '../constants/phases';


interface ParameterGraphProps {
  phaseData: PhaseData[];
  onParamSelect: (params: ParamSelection) => void;
  selectedParams: ParamSelection | null;
}

export const PHASE_COLORS: Record<PhaseKey, string> = {
  stable: "#f8f2da",
  metastable: "#0770b0",
  transition: "#f3c152",
  solitons: "#b24f07",
  unknown: "#8c8c8c",
};

/** Read current component-local 1rem in CSS pixels (scales with .pg font-size). */
const remPxFrom = (el: Element | null): number =>
  parseFloat(getComputedStyle(el ?? document.documentElement).fontSize || '16');

/** Stable key for floating pairs (avoids precision traps). */
const keyOf = (mu: number, sigma: number) => `${mu.toFixed(6)}|${sigma.toFixed(6)}`;

export const ParameterGraph: React.FC<ParameterGraphProps> = ({
  phaseData,
  onParamSelect,
  selectedParams,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLCanvasElement>(null);     // static layer (axes, grid, labels + POINTS BLIT)
  const overlayRef = useRef<HTMLCanvasElement>(null);  // hover + selection only
  const offscreenPointsRef = useRef<HTMLCanvasElement | null>(null); // cached point cloud

  const [hoveredPoint, setHoveredPoint] = useState<HoveredPoint | null>(null);

  // Precompute sorted unique axes
  const muValues = React.useMemo(() => {
    const s = new Set(phaseData.map(p => p.mu));
    return Array.from(s).sort((a, b) => a - b);
  }, [phaseData]);

  const sigmaValues = React.useMemo(() => {
    const s = new Set(phaseData.map(p => p.sigma));
    return Array.from(s).sort((a, b) => a - b);
  }, [phaseData]);

  // Fast phase lookup
  const phaseMap = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of phaseData) m.set(keyOf(p.mu, p.sigma), p.phase_category ?? 'unknown');
    return m;
  }, [phaseData]);

  const getPhaseForPoint = useCallback((mu: number, sigma: number) => {
    return phaseMap.get(keyOf(mu, sigma)) ?? 'unknown';
  }, [phaseMap]);

  // Selected inputs (editable)
  const [muInput, setMuInput] = React.useState('');
  const [sigmaInput, setSigmaInput] = React.useState('');

  useEffect(() => {
    if (selectedParams) {
      setMuInput(selectedParams.mu.toFixed(3));
      setSigmaInput(selectedParams.sigma.toFixed(3));
    } else {
      setMuInput('');
      setSigmaInput('');
    }
  }, [selectedParams]);

  // Canvas config
  const { width: canvasWidth, height: canvasHeight, margin } = CANVAS_CONFIG;
  const plotWidth = canvasWidth - margin.left - margin.right;
  const plotHeight = canvasHeight - margin.top - margin.bottom;

  const minSigma = React.useMemo(() => sigmaValues.length ? Math.min(...sigmaValues) : 0, [sigmaValues]);
  const maxSigma = React.useMemo(() => sigmaValues.length ? Math.max(...sigmaValues) : 1, [sigmaValues]);
  const minMu = React.useMemo(() => muValues.length ? Math.min(...muValues) : 0, [muValues]);
  const maxMu = React.useMemo(() => muValues.length ? Math.max(...muValues) : 1, [muValues]);

  // Scales
  const sigmaToX = useCallback((sigma: number) => {
    if (sigmaValues.length <= 1) return margin.left + plotWidth / 2;
    const dataW = plotWidth - 8;
    return margin.left + 4 + ((sigma - minSigma) / (maxSigma - minSigma)) * dataW;
  }, [margin.left, plotWidth, sigmaValues.length, minSigma, maxSigma]);

  const muToY = useCallback((mu: number) => {
    if (muValues.length <= 1) return margin.top + plotHeight / 2;
    const dataH = plotHeight - 8;
    return margin.top + 4 + ((maxMu - mu) / (maxMu - minMu)) * dataH;
  }, [margin.top, plotHeight, muValues.length, minMu, maxMu]);

  const xToSigma = useCallback((x: number) => {
    if (sigmaValues.length <= 1) return sigmaValues[0] ?? 0;
    const dataW = plotWidth - 8;
    const t = (x - (margin.left + 4)) / dataW;
    const clamped = Math.min(1, Math.max(0, t));
    return minSigma + clamped * (maxSigma - minSigma);
  }, [sigmaValues.length, plotWidth, margin.left, minSigma, maxSigma]);

  const yToMu = useCallback((y: number) => {
    if (muValues.length <= 1) return muValues[0] ?? 0;
    const dataH = plotHeight - 8;
    const t = (y - (margin.top + 4)) / dataH;
    const clamped = Math.min(1, Math.max(0, t));
    return maxMu - clamped * (maxMu - minMu);
  }, [muValues.length, plotHeight, margin.top, minMu, maxMu]);

  // Fast nearest search in sorted array
  const nearestIndex = (arr: number[], value: number) => {
    let lo = 0, hi = arr.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (arr[mid] < value) lo = mid + 1; else hi = mid;
    }
    if (lo > 0 && Math.abs(arr[lo - 1] - value) <= Math.abs(arr[lo] - value)) return lo - 1;
    return lo;
  };

  const getCanvasCoords = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = overlayRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  // Throttle mouse to RAF
  const rafId = useRef<number | null>(null);
  const lastPointer = useRef<{x:number,y:number}|null>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    lastPointer.current = getCanvasCoords(e);
    if (rafId.current == null) {
      rafId.current = requestAnimationFrame(() => {
        rafId.current = null;
        const pt = lastPointer.current!;
        const { x, y } = pt;

        if (x < margin.left || x > margin.left + plotWidth || y < margin.top || y > margin.top + plotHeight) {
          if (hoveredPoint) setHoveredPoint(null);
          return;
        }

        const targetSigma = xToSigma(x);
        const targetMu = yToMu(y);
        const si = nearestIndex(sigmaValues, targetSigma);
        const mi = nearestIndex(muValues, targetMu);

        const snapSigma = sigmaValues[si];
        const snapMu = muValues[mi];
        const sx = sigmaToX(snapSigma);
        const sy = muToY(snapMu);

        const R = remPxFrom(wrapRef.current);
        const hoverRadius = 1.0 * R;

        const dist = Math.hypot(x - sx, y - sy);
        if (dist <= hoverRadius) {
          if (!hoveredPoint || hoveredPoint.mu !== snapMu || hoveredPoint.sigma !== snapSigma) {
            setHoveredPoint({ mu: snapMu, sigma: snapSigma, x: sx, y: sy });
          }
        } else if (hoveredPoint) {
          setHoveredPoint(null);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCanvasCoords, margin.left, margin.top, plotWidth, plotHeight,
      xToSigma, yToMu, sigmaValues, muValues, sigmaToX, muToY, hoveredPoint]);

  useEffect(() => {
    return () => { if (rafId.current != null) cancelAnimationFrame(rafId.current); };
  }, []);

  const handleMouseLeave = useCallback(() => setHoveredPoint(null), []);
  const handleClick = useCallback(() => {
    if (!hoveredPoint) return;
    const phase = getPhaseForPoint(hoveredPoint.mu, hoveredPoint.sigma);
    if (phase === "unknown") return;  // make unclickable
    onParamSelect({ mu: hoveredPoint.mu, sigma: hoveredPoint.sigma });
  }, [hoveredPoint, getPhaseForPoint, onParamSelect]);

  // Steppers
  const bumpMu = React.useCallback((dir: -1 | 1) => {
    if (!selectedParams || muValues.length === 0) return;
    const i = nearestIndex(muValues, selectedParams.mu);
    const j = Math.min(Math.max(i + dir, 0), muValues.length - 1);
    if (j !== i) onParamSelect({ mu: muValues[j], sigma: selectedParams.sigma });
  }, [selectedParams, muValues, onParamSelect]);

  const bumpSigma = React.useCallback((dir: -1 | 1) => {
    if (!selectedParams || sigmaValues.length === 0) return;
    const i = nearestIndex(sigmaValues, selectedParams.sigma);
    const j = Math.min(Math.max(i + dir, 0), sigmaValues.length - 1);
    if (j !== i) onParamSelect({ mu: selectedParams.mu, sigma: sigmaValues[j] });
  }, [selectedParams, sigmaValues, onParamSelect]);

  // Commit typed values (snap to nearest valid step)
  const snapToNearest = (arr: number[], v: number) => {
    if (arr.length === 0) return v;
    const i = nearestIndex(arr, v);
    return arr[i];
  };

  const commitMu = React.useCallback(() => {
    if (!selectedParams) return;
    const v = parseFloat(muInput);
    if (Number.isFinite(v)) {
      const lo = muValues[0] ?? v;
      const hi = muValues[muValues.length - 1] ?? v;
      const clamped = Math.min(Math.max(v, lo), hi);
      const snapped = snapToNearest(muValues, clamped);
      if (Math.abs(snapped - selectedParams.mu) > 1e-12) {
        onParamSelect({ mu: snapped, sigma: selectedParams.sigma });
      } else {
        setMuInput(snapped.toFixed(3));
      }
    } else {
      setMuInput(selectedParams.mu.toFixed(3));
    }
  }, [muInput, selectedParams, muValues, onParamSelect]);

  const commitSigma = React.useCallback(() => {
    if (!selectedParams) return;
    const v = parseFloat(sigmaInput);
    if (Number.isFinite(v)) {
      const lo = sigmaValues[0] ?? v;
      const hi = sigmaValues[sigmaValues.length - 1] ?? v;
      const clamped = Math.min(Math.max(v, lo), hi);
      const snapped = snapToNearest(sigmaValues, clamped);
      if (Math.abs(snapped - selectedParams.sigma) > 1e-12) {
        onParamSelect({ mu: selectedParams.mu, sigma: snapped });
      } else {
        setSigmaInput(snapped.toFixed(3));
      }
    } else {
      setSigmaInput(selectedParams.sigma.toFixed(3));
    }
  }, [sigmaInput, selectedParams, sigmaValues, onParamSelect]);

  // ---------- OFFSCREEN: draw ALL points once ----------
  useEffect(() => {
    if (phaseData.length === 0) return;

    // (Re)allocate offscreen to match device px size
    if (!offscreenPointsRef.current) {
      offscreenPointsRef.current = document.createElement('canvas');
    }
    const off = offscreenPointsRef.current!;
    off.width = canvasWidth;
    off.height = canvasHeight;

    const ctx = off.getContext('2d');
    if (!ctx) return;

    const R = remPxFrom(wrapRef.current);
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // draw only the point cloud (no selection)
    ctx.globalAlpha = 0.95;
    for (let mi = 0; mi < muValues.length; mi++) {
      const mu = muValues[mi];
      for (let si = 0; si < sigmaValues.length; si++) {
        const sigma = sigmaValues[si];
        const phase = getPhaseForPoint(mu, sigma) as keyof typeof PHASE_COLORS;
        const color = PHASE_COLORS[phase] ?? PHASE_COLORS.unknown;

        const x = sigmaToX(sigma);
        const y = muToY(mu);

        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(x, y, 0.25 * R, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
  }, [
    phaseData, muValues, sigmaValues,
    canvasWidth, canvasHeight, getPhaseForPoint, sigmaToX, muToY
  ]);

  // ---------- BASE: axes, grid, labels + BLIT points ----------
  useEffect(() => {
    const base = baseRef.current;
    if (!base || phaseData.length === 0) return;
    const ctx = setupCanvas(base, canvasWidth, canvasHeight);
    if (!ctx) return;

    const R = remPxFrom(wrapRef.current);

    clearCanvas(ctx, canvasWidth, canvasHeight);
    drawBackground(ctx, canvasWidth, canvasHeight);

    // Plot bg
    ctx.fillStyle = '#0b1220';
    ctx.fillRect(margin.left, margin.top, plotWidth, plotHeight);

    // Grid (limit ticks)
    ctx.strokeStyle = '#2b3444';
    ctx.lineWidth = 0.075 * R;
    const sigmaStep = Math.max(1, Math.floor(sigmaValues.length / 10));
    for (let i = 0; i < sigmaValues.length; i += sigmaStep) {
      const x = sigmaToX(sigmaValues[i]);
      ctx.beginPath();
      ctx.moveTo(x, margin.top);
      ctx.lineTo(x, margin.top + plotHeight);
      ctx.stroke();
    }
    const muStep = Math.max(1, Math.floor(muValues.length / 10));
    for (let i = 0; i < muValues.length; i += muStep) {
      const y = muToY(muValues[i]);
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(margin.left + plotWidth, y);
      ctx.stroke();
    }

    // === BLIT PRE-RENDERED POINTS ===
    if (offscreenPointsRef.current) {
      ctx.drawImage(offscreenPointsRef.current, 0, 0);
    }

    // Axes
    ctx.strokeStyle = '#9aa5b1';
    ctx.lineWidth = 0.1 * R;
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top + plotHeight);
    ctx.lineTo(margin.left + plotWidth, margin.top + plotHeight);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(margin.left, margin.top);
    ctx.lineTo(margin.left, margin.top + plotHeight);
    ctx.stroke();

    // Labels & titles
    ctx.fillStyle = '#cbd5e1';
    ctx.font = `${1.0 * R}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace`;
    ctx.textAlign = 'center';
    const sigmaLabelStep = Math.max(1, Math.floor(sigmaValues.length / 5));
    for (let i = 0; i < sigmaValues.length; i += sigmaLabelStep) {
      const x = sigmaToX(sigmaValues[i]);
      ctx.fillText(
        sigmaValues[i].toFixed(3),
        x,
        margin.top + plotHeight + 1.25 * R
      );
    }
    ctx.textAlign = 'right';
    const muLabelStep = Math.max(1, Math.floor(muValues.length / 5));
    for (let i = 0; i < muValues.length; i += muLabelStep) {
      const y = muToY(muValues[i]);
      ctx.fillText(
        muValues[i].toFixed(3),
        margin.left - 0.6 * R,
        y + 0.35 * R
      );
    }

    ctx.font = `${1.2 * R}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace`;
    ctx.textAlign = 'center';
    ctx.fillText('σ', margin.left + plotWidth / 2, margin.top + plotHeight + 2.9 * R);
    ctx.save();
    ctx.translate(1.5 * R, margin.top + plotHeight / 2);
    ctx.fillText('μ', 0, 0);
    ctx.restore();
  }, [
    phaseData,
    canvasWidth, canvasHeight, margin, plotWidth, plotHeight,
    sigmaValues, muValues, sigmaToX, muToY
  ]);

  // ---------- OVERLAY: hover + selection only ----------
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const ctx = setupCanvas(overlay, canvasWidth, canvasHeight);
    if (!ctx) return;

    const R = remPxFrom(wrapRef.current);

    // Clear overlay
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Selected ring (drawn first so hover sits on top)
    if (selectedParams && muValues.length && sigmaValues.length) {
      const sx = sigmaToX(selectedParams.sigma);
      const sy = muToY(selectedParams.mu);

      ctx.beginPath();
      ctx.lineWidth = 0.18 * R;
      ctx.strokeStyle = '#ffffff';
      ctx.arc(sx, sy, 0.65 * R, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Hover effects
    if (!hoveredPoint) return;

    const { x, y, mu, sigma } = hoveredPoint;
    const phase = getPhaseForPoint(mu, sigma) as keyof typeof PHASE_COLORS;
    const color = PHASE_COLORS[phase] ?? PHASE_COLORS.unknown;

    // Crosshair
    ctx.strokeStyle = 'rgba(148, 163, 184, 0.35)';
    ctx.lineWidth = 0.08 * R;
    ctx.setLineDash([0.3 * R, 0.3 * R]);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, margin.top + plotHeight);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(margin.left, y);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Glow + ring
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = 1.1 * R;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, 0.45 * R, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.lineWidth = 0.18 * R;
    ctx.strokeStyle = '#ffffff';
    ctx.arc(x, y, 0.65 * R, 0, Math.PI * 2);
    ctx.stroke();

    // Tooltip (adaptive)
    const tipW = 9.25 * R;
    const tipH = 4.5 * R;
    const pad  = 0.6 * R;
    const gap  = 1.1 * R;

    const spaceRight = (canvasWidth - pad) - x;
    const spaceLeft  = x - pad;
    const spaceAbove = (y - pad) - margin.top;
    const spaceBelow = (margin.top + plotHeight) - (y + pad);

    const placeRight = spaceRight >= tipW + gap || spaceRight >= spaceLeft;
    const baseX = placeRight ? (x + gap) : (x - gap - tipW);

    const placeAbove = spaceAbove >= tipH + gap || spaceAbove >= spaceBelow;
    const baseY = placeAbove ? (y - gap - tipH) : (y + gap);

    const tipX = Math.max(pad, Math.min(baseX, canvasWidth - tipW - pad));
    const tipY = Math.max(pad, Math.min(baseY, canvasHeight - tipH - pad));

    ctx.fillStyle = '#0f172a';
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 0.08 * R;
    ctx.fillRect(tipX, tipY, tipW, tipH);
    ctx.strokeRect(tipX, tipY, tipW, tipH);

    // Connector to box
    ctx.strokeStyle = 'rgba(148,163,184,0.35)';
    ctx.lineWidth = 0.08 * R;
    ctx.beginPath();
    ctx.moveTo(x, y);
    const connectX = x < tipX ? tipX : (x > tipX + tipW ? tipX + tipW : Math.min(Math.max(x, tipX), tipX + tipW));
    const connectY = y < tipY ? tipY : (y > tipY + tipH ? tipY + tipH : Math.min(Math.max(y, tipY), tipY + tipH));
    ctx.lineTo(connectX, connectY);
    ctx.stroke();

    // Text
    ctx.fillStyle = '#e2e8f0';
    ctx.font = `${0.8 * R}px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace`;
    ctx.textAlign = 'left';
    ctx.fillText(`μ = ${mu.toFixed(3)}`, tipX + 0.6 * R, tipY + 1.25 * R);
    ctx.fillText(`σ = ${sigma.toFixed(3)}`, tipX + 0.6 * R, tipY + 2.35 * R);
    ctx.fillStyle = color;
    ctx.fillText(`phase: ${phase}`, tipX + 0.6 * R, tipY + 3.45 * R);
  }, [
    hoveredPoint, selectedParams,
    canvasWidth, canvasHeight, margin, plotWidth, plotHeight,
    muValues, sigmaValues, sigmaToX, muToY, getPhaseForPoint
  ]);

  if (phaseData.length === 0) {
    return <div style={{ padding: '0.75rem 0 1rem 0' }} />;
  }

  // current indices for disabled state
  const muIdx = selectedParams ? nearestIndex(muValues, selectedParams.mu) : -1;
  const sigmaIdx = selectedParams ? nearestIndex(sigmaValues, selectedParams.sigma) : -1;

  return (
    <div ref={wrapRef} className="pg" style={{ padding: '0rem 0 1rem 0' }}>
      {/* Framed graph with legend inside */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            display: 'inline-block',
            background: '#0b1220',
            border: '0.0625rem solid #334155',
            borderRadius: '0.75rem',
            padding: '0.1875rem',
          }}
        >
          <div style={{ position: 'relative' }}>
            <canvas
              ref={baseRef}
              width={canvasWidth}
              height={canvasHeight}
              style={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '0.5rem',
              }}
            />
            <canvas
              ref={overlayRef}
              width={canvasWidth}
              height={canvasHeight}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={handleClick}
              style={{
                position: 'absolute',
                inset: 0,
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
                borderRadius: '0.5rem',
              }}
            />
          </div>

          {/* Legend INSIDE the frame */}
          <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'center' }}>
            <Legend />
          </div>
        </div>
      </div>

      {selectedParams && (
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
          <style>{`
            .pg {
              /* Local UI scale for this component only */
              --pg-scale: 1.18;
              font-size: calc(1rem * var(--pg-scale));
            }
            @media (min-width: 87.5rem) {
              .pg { --pg-scale: 1.25; }
            }
            @media (max-width: 43.75rem) {
              .pg { --pg-scale: 1.1; }
            }

            .sel-wrap {
              display: inline-flex; align-items: center; gap: 0.875rem;
              padding: 0.5rem 0.875rem; border-radius: 0.75rem;
              background: rgba(15,23,42,0.5); border: 0.0625rem solid #334155;
              backdrop-filter: blur(0.3rem); -webkit-backdrop-filter: blur(0.3rem);
            }
            .group { display: inline-flex; align-items: center; gap: 0.5rem; }
            .label { color: #94a3b8; font-size: 1.5rem; letter-spacing: .2em }
            .btns { display: inline-flex; gap: 0.3125rem; }
            .btn {
              width: 2.0rem; height: 2.0rem; border-radius: 0.5rem; border: 0.0625rem solid #3b4a63;
              background: #0b1220; color: #cbd5e1; font-size: 1.5rem; line-height: 1;
              display: grid; place-items: center; cursor: pointer;
            }
            .btn:hover { border-color: #60a5fa; color: #e2e8f0; }
            .btn:disabled { opacity:.45; cursor:not-allowed; }
            .input {
              width: 5.25rem; padding: 0.35rem 0.55rem; border-radius: 0.625rem; outline: none;
              border: 0.0625rem solid #3b4a63; background: #0b1220; color: #e2e8f0;
              font-family: ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,"Liberation Mono",monospace;
              font-size: 1.2rem; text-align: center;
            }
            .input:focus { border-color: #60a5fa; box-shadow: 0 0 0 0.15rem rgba(96,165,250,.16); }
            .sep { width: 0.0625rem; height: 1.25rem; background: rgba(148,163,184,.18); }

            @media (max-width: 45rem) {
              .sel-wrap { gap: 0.6rem; padding: 0.45rem 0.6rem; }
              .input { width: 4.5rem; }
            }
          `}</style>

          <div className="sel-wrap">
            {/* μ controls */}
            <div className="group">
              <span className="label">μ:</span>
              <div className="btns">
                <button className="btn" title="Decrease μ" onClick={() => bumpMu(-1)} disabled={muIdx <= 0}><strong>-</strong></button>
                <button className="btn" title="Increase μ" onClick={() => bumpMu(1)} disabled={muIdx >= muValues.length - 1}><strong>+</strong></button>
              </div>
              <input
                className="input"
                inputMode="decimal"
                value={muInput}
                onChange={(e) => setMuInput(e.target.value)}
                onBlur={commitMu}
                onKeyDown={(e) => { if (e.key === 'Enter') commitMu(); }}
                aria-label="mu value"
              />
            </div>

            <div className="sep" />

            {/* σ controls */}
            <div className="group">
              <span className="label">σ:</span>
              <div className="btns">
                <button className="btn" title="Decrease σ" onClick={() => bumpSigma(-1)} disabled={sigmaIdx <= 0}><strong>-</strong></button>
                <button className="btn" title="Increase σ" onClick={() => bumpSigma(1)} disabled={sigmaIdx >= sigmaValues.length - 1}><strong>+</strong></button>
              </div>
              <input
                className="input"
                inputMode="decimal"
                value={sigmaInput}
                onChange={(e) => setSigmaInput(e.target.value)}
                onBlur={commitSigma}
                onKeyDown={(e) => { if (e.key === 'Enter') commitSigma(); }}
                aria-label="sigma value"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
